import { useMemo, useState } from "react";
import {
  carBrands,
  carModelsByBrand,
  carYears,
  engineTypes,
  coverageTypes,
  type CoverageType,
} from "@data/automotor";
import styles from "./Contact.module.css";

const IconCheck = () => (
  <svg width={12} height={12} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 6.5L5 9.5L10 3.5" />
  </svg>
);

const datalistId = "automotor-modelos";

export function AutomotorFields() {
  const [brand, setBrand] = useState<string>("");
  const [coverage, setCoverage] = useState<Set<CoverageType>>(new Set());
  const [gnc, setGnc] = useState<"si" | "no">("no");

  const models = useMemo(() => carModelsByBrand[brand] ?? [], [brand]);

  function toggleCoverage(v: CoverageType) {
    setCoverage((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  }

  return (
    <fieldset className={styles.condFieldset}>
      <legend className={styles.condLegend}>
        <span className={styles.condLegendDot} /> Datos del vehículo
        <span className={styles.condLegendHint}>
          Cuanto más completes, mejor cotizamos.
        </span>
      </legend>

      <div className={styles.row2}>
        <div className={styles.field}>
          <label htmlFor="car-brand">Marca</label>
          <select
            id="car-brand"
            name="car_brand"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          >
            <option value="">Elegí una marca</option>
            {carBrands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="car-model">Modelo</label>
          <input
            id="car-model"
            name="car_model"
            list={models.length ? datalistId : undefined}
            placeholder={brand ? `Ej. ${models[0] ?? "Modelo"}` : "Elegí marca primero"}
            disabled={!brand}
            autoComplete="off"
          />
          {models.length > 0 && (
            <datalist id={datalistId}>
              {models.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          )}
        </div>
      </div>

      <div className={styles.row2}>
        <div className={styles.field}>
          <label htmlFor="car-year">Año</label>
          <select id="car-year" name="car_year" defaultValue="">
            <option value="" disabled>
              Elegí año
            </option>
            {carYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="car-engine">Tipo de motor</label>
          <select id="car-engine" name="car_engine" defaultValue="">
            <option value="" disabled>
              Elegí motor
            </option>
            {engineTypes.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* GNC */}
      <div className={styles.field}>
        <label className={styles.toggleLabel}>
          ¿Tiene equipo de GNC?
        </label>
        <div className={styles.segmented} role="radiogroup" aria-label="¿Tiene GNC?">
          <label className={`${styles.segmentedOption} ${gnc === "no" ? styles.segmentedActive : ""}`}>
            <input
              type="radio"
              name="car_gnc"
              value="no"
              checked={gnc === "no"}
              onChange={() => setGnc("no")}
            />
            No
          </label>
          <label className={`${styles.segmentedOption} ${gnc === "si" ? styles.segmentedActive : ""}`}>
            <input
              type="radio"
              name="car_gnc"
              value="si"
              checked={gnc === "si"}
              onChange={() => setGnc("si")}
            />
            Sí, tiene GNC
          </label>
        </div>
      </div>

      {/* Cobertura — multi-select chips */}
      <div className={styles.field}>
        <label className={styles.toggleLabel}>
          Tipos de cobertura que te interesan
          <span className={styles.fieldHint}>Podés elegir más de una</span>
        </label>
        <div className={styles.checkChips} role="group" aria-label="Tipos de cobertura">
          {coverageTypes.map((c) => {
            const active = coverage.has(c.value);
            return (
              <label
                key={c.value}
                className={`${styles.checkChip} ${active ? styles.checkChipActive : ""}`}
              >
                <input
                  type="checkbox"
                  name="car_coverage"
                  value={c.value}
                  checked={active}
                  onChange={() => toggleCoverage(c.value)}
                />
                <span className={styles.checkChipBox} aria-hidden>
                  {active && <IconCheck />}
                </span>
                <span className={styles.checkChipText}>
                  <span className={styles.checkChipLabel}>{c.label}</span>
                  <span className={styles.checkChipHint}>{c.hint}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </fieldset>
  );
}
